import { useEffect, useState, useCallback, Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { useDispatch, useSelector } from 'react-redux'
import {
  ConnectorCustomFields,
  CustomFieldType,
  DataTypes,
  validCustomFields,
} from '@components/connector-custom-fields'
import { api } from 'api'
import { showSnackbar } from 'functions'
import { AccountFieldManager, AuthFieldType } from '../components'
import { t } from '@lingui/core/macro'
import { RootState } from '@redux/store'
import { AccountType } from '../types'

interface AdobeExperienceManagerProps {
  newConnection: boolean
  updatedAccount: AccountType
  setRequiredFieldMissing: Dispatch<SetStateAction<boolean>>
  setCustomFieldsInvalid: Dispatch<SetStateAction<boolean>>
  setUpdatedAccount: Dispatch<SetStateAction<AccountType>>
}

const FlexRadioInline = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 10px;
`

export const AdobeExperienceManager = ({
  updatedAccount,
  setUpdatedAccount,
  setRequiredFieldMissing,
  setCustomFieldsInvalid,
  newConnection,
}: AdobeExperienceManagerProps) => {
  const [customFields, setCustomFields] = useState<CustomFieldType[]>([])
  const dispatch = useDispatch()
  const PROXY_URL_ENABLED = Boolean(
    useSelector<RootState>(state => state.Auth.features['aem-proxy-url']),
  )

  const AEMFields: AuthFieldType[] =
    !updatedAccount.metadata.host_type || updatedAccount.metadata.host_type == 'self'
      ? [
          {
            fieldValue: updatedAccount.metadata.username,
            fieldKey: 'username',
            fieldLabel: t`Username`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.username,
          },
          {
            fieldValue: updatedAccount.metadata.password,
            fieldKey: 'password',
            fieldLabel: t`Password`,
            isSecret: true,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.password,
            description: t`The AEM password is securely stored and never surfaced to the user. Entering a new value will override the existing password.`,
          },
          {
            fieldValue: updatedAccount.metadata.url,
            fieldKey: 'url',
            fieldLabel: t`Hosted Url`,
            errorText: t`Invalid Url`,
            hasError: !updatedAccount.metadata.url?.match(/^https:\/\/([\w-]+\.){2,}(\w+)$/g),
          },
          ...(PROXY_URL_ENABLED
            ? [
                {
                  fieldValue: updatedAccount.metadata.proxy_url,
                  fieldKey: 'proxy_url',
                  fieldLabel: t`Proxy Url (optional)`,
                  isOptional: true,
                  description: t`Sometimes the AEM interface is hosted behind a firewall and requires a proxy to access it. This only applies to a minority of users. If you are unsure, Claravine recommends leaving this blank.`,
                },
              ]
            : []),
        ]
      : [
          {
            fieldValue: updatedAccount.metadata.client_id,
            fieldKey: 'client_id',
            fieldLabel: t`Client ID`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.client_id,
            description: t`Security credential file property: integration.technicalAccount.clientId`,
          },
          {
            fieldValue: updatedAccount.metadata.client_secret,
            fieldKey: 'client_secret',
            fieldLabel: t`Client Secret`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.client_secret,
            description: t`Security credential file property: integration.technicalAccount.clientSecret`,
          },
          {
            fieldValue: updatedAccount.metadata.private_key,
            fieldKey: 'private_key',
            fieldLabel: t`Private Key`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.private_key,
            description: t`Security credential file property: integration.privateKey`,
          },
          {
            fieldValue: updatedAccount.metadata.meta_scopes,
            fieldKey: 'meta_scopes',
            fieldLabel: t`Metascopes`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.meta_scopes,
            description: t`Security credential file property: integration.metascopes`,
          },
          {
            fieldValue: updatedAccount.metadata.technical_account_id,
            fieldKey: 'technical_account_id',
            fieldLabel: t`Technical Account ID`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.technical_account_id,
            description: t`Security credential file property: integration.id`,
          },
          {
            fieldValue: updatedAccount.metadata.organization_id,
            fieldKey: 'organization_id',
            fieldLabel: t`Org ID`,
            errorText: t`*Required`,
            hasError: !updatedAccount.metadata.organization_id,
            description: t`Security credential file property: integration.org`,
          },
          {
            fieldValue: updatedAccount.metadata.ims_url,
            fieldKey: 'ims_url',
            fieldLabel: t`IMS Url`,
            errorText: t`Invalid IMS Url`,
            hasError: !updatedAccount.metadata.ims_url?.match(/^https:\/\/([\w-]+\.){2,}(\w+)$/g),
            description: t`Security credential file property: integration.imsEndpoint (Starting with https://)`,
          },
          {
            fieldValue: updatedAccount.metadata.url,
            fieldKey: 'url',
            fieldLabel: t`Hosted Url`,
            errorText: t`Invalid Url`,
            hasError: !updatedAccount.metadata.url?.match(/^https:\/\/([\w-]+\.){2,}(\w+)$/g),
          },
          ...(PROXY_URL_ENABLED
            ? [
                {
                  fieldValue: updatedAccount.metadata.proxy_url,
                  fieldKey: 'proxy_url',
                  fieldLabel: t`Proxy Url (optional)`,
                  isOptional: true,
                  description: t`Sometimes the AEM interface is hosted behind a firewall and requires a proxy to access it. This only applies to a minority of users. If you are unsure, Claravine recommends leaving this blank.`,
                },
              ]
            : []),
        ]

  const updateMetadataHostingType = (value: string) => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        username: '',
        password: '',
        client_id: '',
        metascopes: '',
        technical_account_id: '',
        organization_id: '',
        ims_url: '',
        host_type: value,
      },
    }))
  }

  const updateCustomFields = useCallback(
    (customFields: CustomFieldType[]) => {
      setCustomFields(customFields)
    },
    [customFields],
  )

  const checkForAdobeErrors = () => {
    const customFieldsAreValid = validCustomFields(customFields)

    if (!customFieldsAreValid) {
      setCustomFieldsInvalid(true)
      return
    }
    setCustomFieldsInvalid(false)
  }

  const handleCustomFieldDeletion = useCallback(
    deletedField => {
      if (updatedAccount.deletedFields == undefined) {
        setUpdatedAccount(prevState => ({
          ...prevState,
          deletedFields: [deletedField],
        }))
        return
      }
      setUpdatedAccount(prevState => ({
        ...prevState,
        deletedFields: prevState.deletedFields?.concat([deletedField]),
      }))
    },
    [updatedAccount.deletedFields],
  )

  async function fetchConnectorCustomFields() {
    const result = await api
      .get<
        CustomFieldType[]
      >(`/api/v1/account/connector/${String(updatedAccount.id)}/custom-fields`)
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Unable to load custom fields for your connected AEM integration.`,
            type: 'ERROR',
          }),
        )
      })

    if (result) {
      setCustomFields(result.data)
    }
  }

  useEffect(() => {
    if (!newConnection) {
      updateMetadataHostingType(updatedAccount.metadata.host_type)
      void fetchConnectorCustomFields()
    }
  }, [newConnection])

  useEffect(() => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      customFields,
    }))
  }, [customFields])

  useEffect(() => {
    checkForAdobeErrors()
  }, [customFields])

  return (
    <div>
      <FlexRadioInline
        value={updatedAccount.metadata.host_type ?? 'self'}
        onChange={({ target: { value } }) => {
          updateMetadataHostingType(value)
        }}
      >
        <FormControlLabel value="self" control={<Radio />} label={t`Self hosted`} />
        <FormControlLabel value="cloud" control={<Radio />} label={t`Adobe (cloud) hosted`} />
      </FlexRadioInline>
      <AccountFieldManager
        newConnection={newConnection}
        updatedAccount={updatedAccount}
        setUpdatedAccount={setUpdatedAccount}
        accountFields={AEMFields}
        setRequiredFieldsMissing={setRequiredFieldMissing}
      />
      <ConnectorCustomFields
        connectorId={updatedAccount.id}
        customFields={customFields}
        updateCustomFields={updateCustomFields}
        updateDeletedFields={handleCustomFieldDeletion}
        connectorType="aem"
        usesDataType
        dataTypeOptions={DataTypes['adobe-experience-manager']}
      />
    </div>
  )
}
